import { mutationTypes, actionTypes, errorTypes } from '@/types';
import { logger } from '@/tools';
import { PACKAGE_SYNCER_MODES, syncActionStates, meaning } from '@/tools/consts';
import { i18n } from '@/tools/i18n';

const ignorFilesRegEx = /(control$)/;
async function fetchUrlsFromMd5sums(dataSourceURL, packageName) {
	const response = await fetch(`${dataSourceURL}apps/${packageName}/md5sums`);
	const md5sums = await response.text();
	return md5sums
		.trim()
		.split('\n')
		.filter(line => !ignorFilesRegEx.test(line))
		.map(line => line.split('  .'))
		.reduce(
			(acc, [hash, url]) => Object.assign(acc, { [`${dataSourceURL}apps/${packageName}${url}`]: hash }),
			{}
		);
}

const typeToFile = {
	// We can set this size since imageLoader sets this size when isProgressiveWebApp is true
	image: '1024',
	audio: 'audio.mp3',
};

async function getCacheUrls(state, mode, id, currentLanguage) {
	const { dataSourceURL } = state;
	const { mediaObjectsInfo, offlineAppWithVersionProviderInit } = state.dataSourceCFG;
	let packages = [];
	let mediaObjects = [];
	const urls = {};
	// Get the media objects of the landing exhibition and all calendar events
	if (mode === PACKAGE_SYNCER_MODES.initial || mode === PACKAGE_SYNCER_MODES.all) {
		Object.assign(
			urls,
			...(await Promise.all(
				['calendar-', 'glossary-'].map(_name => {
					const _package = offlineAppWithVersionProviderInit.find(({ name }) => name.startsWith(_name));
					if (!_package) return {};
					return fetchUrlsFromMd5sums(dataSourceURL, _package.name);
				})
			))
		);
		mediaObjects = [
			...mediaObjectsInfo.calendarEvents,
			...mediaObjectsInfo.exhibition[state.dataSourceCFG.landingExhibition.exhibitionID],
		];
		packages = offlineAppWithVersionProviderInit;
	}
	// Get the media object for all exhbitions
	if (mode === PACKAGE_SYNCER_MODES.all) {
		mediaObjects = Object.values(mediaObjectsInfo.exhibition).reduce(
			(acc, _mediaObjects) => [...acc, ..._mediaObjects],
			mediaObjects
		);
		packages = state.dataSourceCFG.exhibitions.reduce(
			(acc, exhibition) => [...acc, ...exhibition.packagesWithVersion],
			packages
		);
	}
	// Get the media objects of the selected tour or exhibiton
	if ([PACKAGE_SYNCER_MODES.exhibition, PACKAGE_SYNCER_MODES.tour].includes(mode)) {
		mediaObjects = mediaObjectsInfo[mode][id];
		if (mode === PACKAGE_SYNCER_MODES.exhibition) {
			packages = state.dataSourceCFG.exhibitionMap[id].packagesWithVersion;
		} else if (mode === PACKAGE_SYNCER_MODES.tour) {
			packages = state.tour.toursMap[id].packagesWithVersion;
		}
	}
	// Get all articles that are not of type builder* and dowload all their md5sums
	// to create all URLs of their content. This is mainly for the custom map articles.
	const nonBuilderArticlesIndex = new Set(
		state.dataSourceCFG.exhibitions.flatMap(exhibition =>
			exhibition.articles.filter(([, { type }]) => !type.includes('builder')).map(([id]) => id)
		)
	);
	const packageNameWhiteListPattern = /(^article-)/;
	return Object.assign(
		urls,
		...(await Promise.all(
			packages
				.filter(({ name }) => name.startsWith('article-') && nonBuilderArticlesIndex.has(name.split('-')[1]))
				.map(({ name }) => fetchUrlsFromMd5sums(dataSourceURL, name))
		)),
		[
			// Get all article urls for the current language
			...packages
				.filter(
					({ name }) =>
						packageNameWhiteListPattern.test(name) && !nonBuilderArticlesIndex.has(name.split('-')[1])
				)
				.map(({ name }) => [`${dataSourceURL}apps/${name}/${currentLanguage}.content`, null]),
			// Convert list of mediaObject into media file URLs.
			...mediaObjects
				.filter(mediaObjectId => {
					const { type } = mediaObjectsInfo.mediaObjects[mediaObjectId];
					const filename = typeToFile[type];
					if (!filename)
						logger.error(
							`MediaObject ${mediaObjectId} with type ${type} could not be translated to a filename.`
						);
					return filename;
				})
				.map(mediaObjectId => [
					`${dataSourceURL}apps/media-${mediaObjectsInfo.mediaObjects[mediaObjectId][currentLanguage]}/${
						typeToFile[mediaObjectsInfo.mediaObjects[mediaObjectId].type]
					}`,
					null,
				]),
		].reduce((acc, [key, val]) => Object.assign(acc, { [key]: val }), {})
	);
}

function createCacheKey([originalUrl, paramValue]) {
	// Create a new URL object to avoid modifying originalUrl.
	var url = new URL(originalUrl);
	if (paramValue) url.search += (url.search ? '&' : '') + '_sw-precache=' + encodeURIComponent(paramValue);
	return url.toString();
}

async function cacheUrls({ urlItems, dispatch, commit, openUiLoader = false }) {
	const existingRequests = await cache.keys();
	const cachedUrls = new Set(existingRequests.map(({ url }) => url));
	// Organize the URLs in packages of 50 URLs to not overwhelm the browser.
	const urlBins = Object.entries(urlItems)
		// remove URLs that are already cached
		.filter(urlItem => !cachedUrls.has(createCacheKey(urlItem)))
		.reduce((acc, urlItem, index) => {
			if (index === 0 || index % 50 === 0) acc.push([]);
			acc[acc.length - 1].push(urlItem);
			return acc;
		}, []);
	const failedBins = [];
	const newCachedUrls = [];
	if (openUiLoader) dispatch(actionTypes.openModal, { id: 'ui--loader' });
	for (var i = 0; i < urlBins.length; i++) {
		const _urls = urlBins[i].map(createCacheKey);
		try {
			await cache.addAll(_urls); // cache missing URLs
			newCachedUrls.push(urlBins[i]);
		} catch (error) {
			failedBins.push(urlBins[i]);
		}
		commit(mutationTypes.packageSyncerSetProgress, { appsProcessed: i, appsTotal: urlBins.length });
		logger.log(`Cache bin ${i + 1}/${urlBins.length}`);
	}
	const errors = [];
	const retryUrls = failedBins.flat();
	for (var j = 0; j < retryUrls.length; j++) {
		try {
			await cache.add(createCacheKey(retryUrls[j])); // cache missing URLs
			newCachedUrls.push([retryUrls[j]]);
		} catch (error) {
			errors.push(retryUrls[j][0]);
		}
		commit(mutationTypes.packageSyncerSetProgress, { appsProcessed: j, appsTotal: retryUrls.length });
		logger.log(`Cache bin ${j + 1}/${retryUrls.length}`);
	}

	// Store all cached urls in the local storage.
	localStorage.cachedUrls = JSON.stringify(
		newCachedUrls
			.flat()
			.filter(([, hash]) => hash)
			.reduce(
				(acc, [url, hash]) => Object.assign(acc, { [url]: hash }),
				JSON.parse(localStorage.cachedUrls || '{}')
			)
	);
	if (openUiLoader) dispatch(actionTypes.closeModal, { id: 'ui--loader' });

	// Raise error for all failed downloads
	if (errors.length) throw new Error(`Could not download \n${errors.join('\n')}`);
}

function saveStateToLocalStorage(subPartStates, currentLanguage) {
	// Update the local storage with the current state for the next session
	localStorage.selectedSubParts = JSON.stringify(
		Object.entries(subPartStates)
			.filter(([, subPartState]) => subPartState === syncActionStates.loaded)
			.reduce((acc, [subPartId]) => Object.assign(acc, { [subPartId]: currentLanguage }), {})
	);
}

let cache = null;
let mode = null;

export default {
	actions: {
		async [actionTypes.progressiveWebAppInit](
			{ rootState, getters, commit, dispatch },
			{ serviceWorkerRegistration }
		) {
			serviceWorkerRegistration.active.postMessage({
				type: 'updatePrecacheManifest',
				precacheManifest: Object.entries(JSON.parse(localStorage.cachedUrls || '{}')),
			});
			// If we are offline, do not try to sync.
			if (!navigator.onLine) {
				logger.warn('You are offline, can not sync.');
				return;
			}
			({ mode } = rootState.dataSourceCFG.configWebClient.packageSyncer);
			const { currentLanguage } = getters;
			cache = await caches.open(`shoutrlabs-webclient-sw-${rootState.dataSourceURL}`);
			// Initalize the sub part states from the local storage
			const selectedSubParts = localStorage.selectedSubParts ? JSON.parse(localStorage.selectedSubParts) : {};
			Object.entries(selectedSubParts).forEach(([subPartId, state]) => {
				commit(mutationTypes.packageSyncerSetSubPartState, {
					subPartId,
					subPartState: state === currentLanguage ? syncActionStates.loaded : null,
				});
			});
			// Get the URLs that should be cached on startup
			const initialCachedUrls = await getCacheUrls(
				rootState,
				mode === PACKAGE_SYNCER_MODES.all ? mode : PACKAGE_SYNCER_MODES.initial,
				null,
				currentLanguage
			);
			// Clean up the cache and cache missing URLs.
			const selectedSubPartUrls = await Promise.all(
				// get the URLs from all selected sub parts.
				Object.entries(selectedSubParts)
					.filter(([, state]) => state === currentLanguage)
					.map(([subPartId]) => getCacheUrls(rootState, mode, subPartId, currentLanguage))
			);
			const urlsThatShouldBeCached = Object.assign(initialCachedUrls, ...selectedSubPartUrls);
			if (Object.keys(urlsThatShouldBeCached).length) {
				commit(mutationTypes.sendMessage, {
					type: meaning.info,
					body: i18n.t(`error.${errorTypes.pwaInitalSync}`),
				});
			}
			const urlsThatShouldBeCachedIndex = new Set(Object.entries(urlsThatShouldBeCached).map(createCacheKey));
			try {
				await cacheUrls({ urlItems: urlsThatShouldBeCached, dispatch, commit, openUiLoader: true });
			} catch (error) {
				logger.error('error', error);
				dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.packageSyncerDownloadError) });
			}
			// Remove URLs from cache that are not any more needed.
			const existingRequests = await cache.keys();
			const ignoredUrlsPatterns = /(\/cache\/)|(webapp-navigation-bar)|(style-)/;
			await Promise.all(
				existingRequests
					.filter(({ url }) => !urlsThatShouldBeCachedIndex.has(url) && !ignoredUrlsPatterns.test(url))
					.map(async existingRequest => {
						return await cache.delete(existingRequest);
					})
			);
		},

		// Cache a subpart.
		async [actionTypes.progressiveWebAppLoad]({ rootState, getters, commit, dispatch }, { subPartId }) {
			if (!navigator.onLine) {
				logger.error('You are offline, can not sync.');
				dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.packageSyncerDownloadError) });
				return;
			}
			commit(mutationTypes.packageSyncerSetSubPartState, {
				subPartId,
				subPartState: syncActionStates.loading,
			});
			try {
				await cacheUrls({
					urlItems: await getCacheUrls(rootState, mode, subPartId, getters.currentLanguage),
					commit,
				});
				commit(mutationTypes.packageSyncerSetSubPartState, {
					subPartId,
					subPartState: syncActionStates.loaded,
				});
			} catch (error) {
				logger.error('error', error);
				dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.packageSyncerDownloadError) });
				commit(mutationTypes.packageSyncerSetSubPartState, {
					subPartId,
					subPartState: syncActionStates.loadIncomplete,
				});
			}
			saveStateToLocalStorage(rootState.packageSyncer.subPartStates, getters.currentLanguage);
		},

		// Delete a subpart from the cache.
		async [actionTypes.progressiveWebAppDelete]({ rootState, getters, commit }, { subPartId }) {
			commit(mutationTypes.packageSyncerSetSubPartState, {
				subPartId,
				subPartState: syncActionStates.deleting,
			});
			const urlToDeleteIndex = new Set(
				Object.entries(await getCacheUrls(rootState, mode, subPartId, getters.currentLanguage)).map(
					createCacheKey
				)
			);
			const existingRequests = await cache.keys();
			await Promise.all(
				existingRequests.map(async existingRequest => {
					if (urlToDeleteIndex.has(existingRequest.url)) {
						return await cache.delete(existingRequest);
					}
				})
			);
			commit(mutationTypes.packageSyncerSetSubPartState, { subPartId, subPartState: null });
			saveStateToLocalStorage(rootState.packageSyncer.subPartStates, getters.currentLanguage);
		},
	},
};
